import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';

const HansenResultGroundFactors = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const { beta } = result.input;

  const { canUserSeeCalculationSteps, isInclinedLoading, isPhiZero, gc, gq, gGamma } = result.output;
  const case1 = isInclinedLoading && isPhiZero;
  const case2 = isInclinedLoading && !isPhiZero;
  const case3 = !isInclinedLoading && isPhiZero;
  const case4 = !isInclinedLoading && !isPhiZero;
  const caseLegendItems = [
    { label: 'CASE 1', description: 'Inclined loading with \u03c6 = 0', isActive: case1 },
    { label: 'CASE 2', description: 'Inclined loading with \u03c6 \u2260 0', isActive: case2 },
    { label: 'CASE 3', description: 'No inclined loading with \u03c6 = 0', isActive: case3 },
    { label: 'CASE 4', description: 'No inclined loading with \u03c6 \u2260 0', isActive: case4 },
  ];
  const [showCase13gcDetails, setShowCase13gcDetails] = useState(false);
  const [showCase24gqDetails, setShowCase24gqDetails] = useState(false);
  const [showCase24gcDetails, setShowCase24gcDetails] = useState(false);
  const [showCase24ggammaDetails, setShowCase24ggammaDetails] = useState(false);

  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const successAlertSx = {
    backgroundColor: 'rgba(46,125,50,0.04)',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      alignSelf: 'flex-start',
      mt: 0.25,
      minWidth: 24,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      mt: 0.25,
      width: 32,
      justifyContent: 'center',
      p: 0,
      ml: 1,
    },
    '& .MuiAlert-message': { width: '100%' },
  };

  const renderAlertAction = (isExpanded, setExpanded) =>
    canUserSeeCalculationSteps ? (
      <IconButton size="small" color="inherit" onClick={() => setExpanded((v) => !v)} sx={{ width: 32, height: 32 }}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    ) : (
      <Tooltip
        title={
          <span>
            Upgrade to unlock calculation details.{' '}
            <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
              See plans
            </Link>
          </span>
        }
        leaveDelay={500}
        arrow
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'text.disabled',
            cursor: 'not-allowed',
            mr: 1,
            mt: 0.5,
          }}
        />
      </Tooltip>
    );

  const Case1Case3gcDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        &phi; = 0 &rarr; Only g'<sub>c</sub> should be calculated.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        g'<sub>c</sub> = &beta; / 147
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        g'<sub>c</sub> = {round(beta, 2)} / 147
      </Typography>
      <Typography variant="body2" color="text.secondary">
        g'<sub>c</sub>={round(gc, 2)}
      </Typography>
    </>
  );

  const Case2Case4gqDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        g<sub>q</sub> = (1 - 0.5 tan&beta;)<sup>5</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        g<sub>q</sub> = (1 - 0.5 tan{round(beta, 2)})<sup>5</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        g<sub>q</sub>={round(gq, 2)}
      </Typography>
    </>
  );

  const Case2Case4gcDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        g<sub>c</sub> = 1 - &beta; / 147
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        g<sub>c</sub> = 1 - {round(beta, 2)} / 147
      </Typography>
      <Typography variant="body2" color="text.secondary">
        g<sub>c</sub>={round(gc, 2)}
      </Typography>
    </>
  );

  const Case2Case4ggammaDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        g<sub>&gamma;</sub> = (1 - 0.5 tan&beta;)<sup>5</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        g<sub>&gamma;</sub> = (1 - 0.5 tan{round(beta, 2)})<sup>5</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        g<sub>&gamma;</sub>={round(gGamma, 2)}
      </Typography>
    </>
  );

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Calculation of Hansen Ground Factors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes Hansen ground factors (g<sub>c</sub>, g<sub>q</sub>, and g<sub>&gamma;</sub>) based on load
          inclination and friction-angle conditions. The 4 cases that can happen based on these conditions are listed below:
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Case Legend
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0.8 }}>
            {caseLegendItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.7,
                  backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                }}
              >
                {renderCaseBadge(item.label, {
                  ml: 0,
                  minWidth: 64,
                  justifyContent: 'center',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  color: item.isActive ? 'success.dark' : 'text.disabled',
                  backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                  fontWeight: item.isActive ? 700 : 600,
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Case 1/3 gc Factor */}
          {(case1 || case3) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase13gcDetails, setShowCase13gcDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Ground factor (g'<sub>c</sub>) = {round(gc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase13gcDetails} unmountOnExit>
                  <Case1Case3gcDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2/4 gq Factor */}
          {(case2 || case4) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase24gqDetails, setShowCase24gqDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Ground factor (g<sub>q</sub>) = {round(gq, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase24gqDetails} unmountOnExit>
                  <Case2Case4gqDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2/4 gc Factor */}
          {(case2 || case4) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase24gcDetails, setShowCase24gcDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Ground factor (g<sub>c</sub>) = {round(gc, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase24gcDetails} unmountOnExit>
                  <Case2Case4gcDetails />
                </Collapse>
              )}
            </Alert>
          )}

          {/* Success Alert: Case 2/4 g_gamma Factor */}
          {(case2 || case4) && (
            <Alert
              icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
              severity="success"
              variant="outlined"
              sx={successAlertSx}
              action={renderAlertAction(showCase24ggammaDetails, setShowCase24ggammaDetails)}
            >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  Ground factor (g<sub>&gamma;</sub>) = {round(gGamma, 2)}
                </b>
              </Typography>

              {canUserSeeCalculationSteps && (
                <Collapse in={showCase24ggammaDetails} unmountOnExit>
                  <Case2Case4ggammaDetails />
                </Collapse>
              )}
            </Alert>
          )}
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultGroundFactors;
